<template>
  <div class="vote-page">
    <!-- ===== 加载中遮罩（checkVoted 完成前不显示投票界面） ===== -->
    <div v-if="pageLoading" class="page-loading-overlay">
      <div class="page-loading-spinner"></div>
      <p class="page-loading-text">{{ currentLang === 'zh' ? '加载中...' : 'Memuat...' }}</p>
    </div>

    <!-- ===== 投票内容（只在 pageLoading 完成后显示） ===== -->
    <template v-if="!pageLoading">
    <!-- ===== 顶部导航 ===== -->
    <div class="top-bar">
      <div class="top-bar-row">
        <span class="brand"><img src="../assets/left-right.png" class="brand-logo" /> IPIP园区</span>
        <span class="lang-switch">
          <span class="lang-slider" :class="currentLang === 'id' ? 'slide-id' : 'slide-zh'"></span>
          <a class="lang-opt" :class="{ active: currentLang === 'id' }" href="?lang=id" @click.prevent="switchLang('id')" @touchstart.prevent="switchLang('id')">ID</a>
          <a class="lang-opt" :class="{ active: currentLang === 'zh' }" href="?lang=zh" @click.prevent="switchLang('zh')" @touchstart.prevent="switchLang('zh')">中文</a>
        </span>
        <span class="top-actions">
          <span class="rule-btn" @click="showRules = true">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>
            {{ currentLang === 'zh' ? '规则' : 'Aturan' }}
          </span>
        </span>
      </div>
      <div class="countdown-tag" v-if="statusInfo">
        <span class="cd-label">{{ statusInfo.label }}</span>
        <span class="cd-time" v-if="statusInfo.countdown">{{ statusInfo.countdown }}</span>
      </div>
    </div>

    <!-- ===== 标题区 ===== -->
    <div class="header">
      <div class="header-glow"></div>
      <span class="header-particle" style="top:15%;left:10%;animation-delay:0s;"></span>
      <span class="header-particle" style="top:20%;right:15%;animation-delay:1.2s;"></span>
      <span class="header-particle" style="top:55%;left:8%;animation-delay:0.8s;"></span>
      <span class="header-particle" style="top:60%;right:12%;animation-delay:2s;"></span>
      <span class="header-particle" style="top:30%;left:50%;animation-delay:1.6s;"></span>
      <span class="header-particle" style="top:70%;right:25%;animation-delay:2.8s;"></span>
      <span class="header-particle" style="top:40%;left:20%;animation-delay:0.4s;"></span>
      <span class="header-particle" style="top:75%;left:60%;animation-delay:2.4s;"></span>
      <h1 class="title"><span class="title-trophy">🏆</span><span class="title-text">{{ titleParts[0] }}</span><span class="title-trophy">🏆</span><span v-if="titleParts.length > 1" class="title-text title-text-last">{{ titleParts[1] }}</span></h1>
      <p class="subtitle">Pemungutan Suara Karyawan Teladan · IPIP 2026</p>
      <div class="stats-row">
        <div class="stat"><span class="stat-num">{{ candidates.length }}</span><span class="stat-label">{{ $t('vote.candidates') }}</span></div>
        <div class="stat-divider"></div>
        <div class="stat"><span class="stat-num">{{ companies.length }}</span><span class="stat-label">{{ $t('vote.companies') }}</span></div>
        <div class="stat-divider"></div>
        <div class="stat"><span class="stat-num">10</span><span class="stat-label">{{ $t('vote.required') }}</span></div>
      </div>
    </div>

    <!-- ===== 公司配额标签 ===== -->
    <div class="quota-tabs">
      <span
        v-for="(c, i) in companies" :key="i"
        :class="['quota-tab', { active: activeCompany === i, 'tab-bounce': bounceTab === i }]"
        @click="onTabClick(i)"
      ><span class="quota-name">{{ getCompanyName(c) }}</span><em>{{ selectedByCompany(c.key) }}/{{ c.max }}</em></span>
    </div>

    <!-- ===== 候选人列表（按公司分组滚动） ===== -->
    <div class="candidate-area" ref="listArea">
      <div v-for="(comp, ci) in companies" :key="ci" class="company-group" :ref="'group-' + ci">
        <div class="group-header">
          <span class="group-name">{{ getCompanyName(comp) }}</span>
          <span class="group-count">{{ selectedByCompany(comp.key) }}/{{ comp.max }}</span>
        </div>
        <div class="candidate-grid">
          <div
            v-for="(c, idx) in comp.candidates" :key="c.id"
            :class="['candidate-card', { selected: isSelected(c.id) }]"
            :style="getCardStyle(idx)"
            @click="toggleCandidate(c)"
          >
            <!-- 电路纹理装饰 -->
            <svg class="card-circuit" viewBox="0 0 110 110" preserveAspectRatio="none">
              <path d="M5 30 L25 30 L30 25 L30 5" stroke="rgba(218,165,32,0.15)" stroke-width="0.8" fill="none"/>
              <path d="M105 50 L90 50 L85 55 L85 70" stroke="rgba(255,215,0,0.12)" stroke-width="0.8" fill="none"/>
              <path d="M5 80 L20 80 L25 75 L25 60" stroke="rgba(218,165,32,0.1)" stroke-width="0.6" fill="none"/>
              <circle cx="5" cy="30" r="1.5" fill="rgba(218,165,32,0.5)"/>
              <circle cx="30" cy="5" r="1.5" fill="rgba(218,165,32,0.5)"/>
              <circle cx="105" cy="50" r="1.5" fill="rgba(255,215,0,0.5)"/>
              <circle cx="85" cy="70" r="1.5" fill="rgba(255,215,0,0.5)"/>
              <circle cx="5" cy="80" r="1.5" fill="rgba(218,165,32,0.4)"/>
              <circle cx="25" cy="60" r="1.5" fill="rgba(218,165,32,0.4)"/>
            </svg>
            <!-- 悬浮晶体 -->
            <div class="card-crystal crystal-1"></div>
            <div class="card-crystal crystal-2"></div>
            <!-- 头像与详情按钮 -->
            <div class="card-header-row">
              <div class="card-avatar-box" :style="{ backgroundImage: 'url(' + getAvatar(c) + ')' }">
              </div>
              <span v-if="isSelected(c.id)" class="card-check">✓</span>
              <span class="card-detail" @click.stop="showDetail(c)">{{ $t('vote.detail') }}</span>
            </div>
            <div class="card-info">
              <span class="card-name">{{ currentLang === 'zh' ? c.name : c.nameId }}</span>
              <div class="card-meta-row">
                <span class="card-code">{{ c.code }}</span>
                <span class="card-meta-dot">·</span>
                <span class="card-dept">{{ getDeptText(c) }}</span>
              </div>
              <div v-if="hasVoted || !canVoteNow" class="card-votes">
                <span class="vote-icon">🔥</span>
                <span class="vote-count">{{ c.voteCount || 0 }}</span>
                <span class="vote-label">{{ currentLang === 'zh' ? '票' : 'Suara' }}</span>
              </div>
            </div>
            <!-- 底部反射 -->
            <div class="card-reflection"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== 规则提示 ===== -->
    <div class="rules-tip">
      <p v-if="currentLang === 'zh'">一账号一票 · 提交后不可修改 · 系统监测异常刷票</p>
      <p v-else>Satu akun satu suara · Terkunci · Anti-cheat</p>
    </div>

    <!-- ===== 底部进度条 ===== -->
    <div class="bottom-bar">
      <div class="progress-header">
        <span>{{ $t('vote.progress') }}</span>
        <span class="progress-count">{{ selectedCount }} / 10</span>
      </div>
      <div class="progress-bar-wrap">
        <div class="progress-bar" :style="{ width: (selectedCount / 10 * 100) + '%' }"></div>
      </div>
      <div class="progress-quotas">
        <span v-for="(c, ci) in companies" :key="c.key" class="quota-pill">
          <span class="quota-dot" :style="{ background: companyColors[ci] }"></span>
          {{ getCompanyName(c) }}{{ selectedByCompany(c.key) }}/{{ c.max }}
        </span>
      </div>
      <button
        :class="['submit-btn', { active: (hasVoted || !canVoteNow) || selectedCount >= 10, voted: hasVoted }]"
        :disabled="!hasVoted && !canVoteNow ? false : (!hasVoted && selectedCount < 10)"
        @click="submitClick"
        ref="submitBtn"
      >{{ hasVoted ? (currentLang === 'zh' ? '查看中奖记录' : 'Lihat Rekor Hadiah') : (!canVoteNow ? (currentLang === 'zh' ? '投票已结束' : 'Voting Berakhir') : (selectedCount >= 10 ? $t('vote.submit') : $t('vote.needMore') + ' ' + (10 - selectedCount) + ' ' + $t('vote.voteUnit'))) }}</button>
    </div>

    <!-- ===== 详情弹窗 ===== -->
    <div v-if="detailCandidate" class="detail-overlay" @click="detailCandidate = null">
      <div class="detail-page" @click.stop>
        <div class="detail-close" @click="detailCandidate = null">✕</div>
        <!-- 视频区域 -->
        <div class="detail-video">
          <video
            class="detail-video-player"
            :src="detailVideo"
            :poster="detailCandidate.poster"
            controls
            playsinline
            preload="metadata"
          ></video>
        </div>
        <!-- 信息区域 -->
        <div class="detail-body">
          <h2 class="detail-name">{{ currentLang === 'zh' ? detailCandidate.name : detailCandidate.nameId }}</h2>
          <p class="detail-name-cn">{{ currentLang === 'zh' ? detailCandidate.nameId : detailCandidate.name }}</p>
          <p class="detail-meta">
            🪪 {{ currentLang === 'zh' ? '工号' : 'No. Karyawan' }}: {{ detailCandidate.code }} | {{ getDeptText(detailCandidate) }}
          </p>
          <p class="detail-position" v-if="detailCandidate.position">
            💼 {{ detailCandidate.position }}
          </p>
          <div class="detail-divider"></div>
          <p class="detail-section-title">{{ currentLang === 'zh' ? '个人介绍' : 'Perkenalan Pribadi' }}</p>
          <p class="detail-desc">{{ getDescText(detailCandidate) }}</p>
          <div class="detail-video-hint">
            <span class="video-hint-text">{{ currentLang === 'zh' ? '📹 自我介绍视频' : '📹 Video Perkenalan Diri' }}</span>
          </div>
        </div>
        <!-- 投票按钮 -->
        <div class="detail-vote-area">
          <button class="detail-vote-btn" @click="voteFromDetail(detailCandidate)">
            {{ isSelected(detailCandidate.id) ? (currentLang === 'zh' ? '已选择' : 'Dipilih') : (currentLang === 'zh' ? '投票' : 'Pilih') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ===== 确认投票弹窗 ===== -->
    <div v-if="showConfirmDialog" class="confirm-overlay">
      <div class="confirm-dialog" @click.stop>
        <h2 class="confirm-title">{{ currentLang === 'zh' ? '确认投票' : 'Konfirmasi Voting' }}</h2>
        <p class="confirm-sub">{{ currentLang === 'zh' ? '提交后不可修改' : 'Tidak dapat diubah setelah dikirim' }}</p>
        <div class="confirm-scroll-area">
          <div class="confirm-list">
            <div v-for="comp in allCompanies" :key="comp.key" class="confirm-company">
              <div class="confirm-company-header">
                <span class="confirm-company-name">{{ getCompanyName(comp) }}（{{ selectedByCompany(comp.key) }}/{{ comp.max }}）</span>
              </div>
              <div v-for="c in comp.selectedCandidates" :key="c.id" class="confirm-candidate">
                <div class="confirm-cand-avatar" :style="{ backgroundImage: 'url(' + getAvatar(c) + ')' }">
                </div>
                <span class="confirm-cand-info">
                  <span class="confirm-cand-name">{{ currentLang === 'zh' ? c.name : c.nameId }}</span>
                  <span class="confirm-cand-name-id">{{ currentLang === 'zh' ? c.nameId : c.name }}</span>
                </span>
                <span class="confirm-cand-code">{{ c.code }}</span>
              </div>
            </div>
          </div>
          <p class="confirm-lottery-hint">{{ currentLang === 'zh' ? '🎁 提交后自动进入抽奖' : '🎁 Undian otomatis setelah dikirim' }}</p>
        </div>
        <div class="confirm-btns">
          <button class="confirm-btn-back" @click="showConfirmDialog = false">{{ currentLang === 'zh' ? '返回' : 'Kembali' }}</button>
          <button class="confirm-btn-submit" @click="submitVote">{{ currentLang === 'zh' ? '确认提交' : 'Kirim' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== 开奖弹窗（投票后展示） ===== -->
    <div v-if="showLotteryDialog" class="lottery-overlay" @click="showLotteryDialog = false">
      <div class="lottery-card" @click.stop>
        <div class="lottery-ribbon">🎉</div>
        <!-- 中奖 -->
        <template v-if="prizeRecord && prizeRecord.won">
          <p class="lottery-win-text">{{ currentLang === 'zh' ? '投票成功！恭喜您中奖！' : 'Voting berhasil! Selamat, Anda memenangkan hadiah!' }}</p>
          <p class="lottery-amount-text">{{ currentLang === 'zh' ? '中奖金额：' + prizeRecord.amount + ' IDR' : 'Jumlah hadiah: ' + prizeRecord.amount + ' IDR' }}</p>
          <p class="lottery-contact">{{ currentLang === 'zh' ? '联系IPIP园区人力资源部（印尼）：Femyanastasya 菲美领取' : 'Hubungi Departemen SDM IPIP Park (Indonesia): Femyanastasya' }}</p>
        </template>
        <!-- 未中奖 -->
        <template v-else>
          <p class="lottery-lose-text">{{ currentLang === 'zh' ? '投票成功！很遗憾，您未中奖。' : 'Voting berhasil! Sayang sekali, Anda tidak memenangkan hadiah.' }}</p>
          <p class="lottery-lose-sub">{{ currentLang === 'zh' ? '感谢您的参与！' : 'Terima kasih atas partisipasi Anda!' }}</p>
        </template>
        <button class="lottery-btn" @click="showLotteryDialog = false">{{ currentLang === 'zh' ? '查看投票结果' : 'Lihat Hasil' }}</button>
      </div>
    </div>

    <!-- ===== 中奖记录弹窗 ===== -->
    <div v-if="showPrizeDialog" class="lottery-overlay" @click="showPrizeDialog = false">
      <div class="lottery-card" @click.stop>
        <div class="lottery-ribbon">🎉</div>
        <template v-if="prizeRecord && prizeRecord.won">
          <p class="lottery-win-text">{{ currentLang === 'zh' ? '恭喜您中奖！' : 'Selamat, Anda menang!' }}</p>
          <p class="lottery-amount-text">{{ currentLang === 'zh' ? '中奖金额：' + prizeRecord.amount + ' IDR' : 'Jumlah hadiah: ' + prizeRecord.amount + ' IDR' }}</p>
          <p class="lottery-contact">{{ currentLang === 'zh' ? '联系IPIP园区人力资源部（印尼）：Femyanastasya 菲美领取' : 'Hubungi Departemen SDM IPIP Park (Indonesia): Femyanastasya' }}</p>
          <button
            v-if="!prizeRecord.claimed"
            class="lottery-btn claim-btn"
            @click="handleClaimPrize"
          >{{ currentLang === 'zh' ? '未核销' : 'Belum Diklaim' }}</button>
          <button
            v-else
            class="lottery-btn claimed-btn"
            disabled
          >{{ currentLang === 'zh' ? '已核销' : 'Sudah Diklaim' }}</button>
        </template>
        <template v-else>
          <p class="lottery-lose-text">{{ currentLang === 'zh' ? '很遗憾，您未中奖' : 'Sayang sekali, Anda tidak menang' }}</p>
          <p class="lottery-lose-emoji">😢</p>
        </template>
      </div>
    </div>

    <!-- ===== 规则弹窗 ===== -->
    <div v-if="showRules" class="rules-overlay" @click="showRules = false">
      <div class="rules-dialog" @click.stop>
        <div class="rules-dialog-title">{{ $t('rulesDialog.title') }}</div>
        <div class="rules-dialog-sub">{{ $t('rulesDialog.sub') }}</div>
        <div class="rules-dialog-list">
          <div v-for="(r, i) in rulesDialogList" :key="i" class="rules-dialog-item">
            <span class="rules-dialog-num">{{ String(i + 1).padStart(2, '0') }}</span>
            <div class="rules-dialog-body">
              <p class="rules-dialog-text">{{ currentLang === 'zh' ? r.zh : r.id }}</p>
            </div>
          </div>
        </div>
        <div class="rules-dialog-lottery">
          🎁 {{ $t('rulesDialog.lottery') }}
        </div>
        <button class="rules-dialog-btn" @click="showRules = false">{{ currentLang === 'zh' ? '我知道了' : 'Saya mengerti' }}</button>
      </div>
    </div>

    <!-- Toast 提示 -->
    </template>
    <transition name="toast-fade">
      <div class="toast" v-if="showToast">{{ toastMsg }}</div>
    </transition>

    <!-- ===== 加载中遮罩（checkVoted 完成前不显示投票界面） ===== -->
    <div v-if="pageLoading" class="page-loading-overlay">
      <div class="page-loading-spinner"></div>
      <p class="page-loading-text">{{ currentLang === 'zh' ? '加载中...' : 'Memuat...' }}</p>
    </div>
  </div>
</template>

<script>
import { t, getLang, setLang } from '../utils/i18n'
import { submitVote as submitVoteApi, checkVoted, getCandidates, saveLotteryResult, claimLotteryPrize, drawLotteryV2, getLotteryRecord } from '../api/vote'
import { drawLottery as frontendDrawLottery, saveUserRecord, getUserRecord, claimPrize } from '../utils/lottery'
import { canVote, getStatusInfo } from '../utils/voteTime'
import { REAL_CANDIDATES } from '../data/realCandidates'
import { getAuthCode, isInDingTalk } from '../utils/dingtalk'
import { ssoLogin, checkAuth } from '../api/auth'

// 公司配置（无论中文还是印尼语界面，公司名称都统一显示英文代码）
const COMPANY_CONFIG = {
  IPIP: { key: 'IPIP', name: 'IPIP', max: 3, idName: 'IPIP' },
  NJ: { key: 'NJ', name: 'NJ', max: 1, idName: 'NJ' },
  HXNI: { key: 'HXNI', name: 'HXNI', max: 3, idName: 'HXNI' },
  KNI: { key: 'KNI', name: 'KNI', max: 2, idName: 'KNI' },
  HBB: { key: 'HBB', name: 'HBB', max: 1, idName: 'HBB' },
}

export default {
  name: 'VotePage',
  data () {
    return {
      candidates: [],
      activeCompany: 0,
      bounceTab: -1,
      selectedIds: [],
      detailCandidate: null,
      defaultAvatar: 'this.src="' + require('../img/defaultImg.png') + '"',
      defaultAvatarImg: require('../img/defaultImg.png'),
      hasVoted: false,
      showRules: false,
      showConfirmDialog: false,
      showLotteryDialog: false,
      showPrizeDialog: false,
      prizeRecord: null,
      companyColors: ['#1677ff', '#f44336', '#ff9800', '#673ab7', '#795548'],
      // 投票时间控制
      canVoteNow: true,
      statusInfo: null,
      showToast: false,
      toastMsg: '',
      toastTimer: null,
      countdownTimer: null,
      currentLang: getLang(),
      // 页面加载遮罩：checkVoted 完成前隐藏投票界面
      pageLoading: true,
    }
  },
  computed: {
    companies () {
      const grouped = {}
      this.candidates.forEach(c => {
        const key = c.company
        if (!grouped[key]) {
          const config = COMPANY_CONFIG[key] || { key, name: key, max: 10, idName: key }
          grouped[key] = { ...config, candidates: [] }
        }
        grouped[key].candidates.push(c)
      })
      // 按固定顺序排序：IPIP、NJ、HXNI、KNI、HBB
      const order = ['IPIP', 'NJ', 'HXNI', 'KNI', 'HBB']
      return order.map(key => grouped[key]).filter(Boolean)
    },
    selectedCount () {
      return this.selectedIds.length
    },
    rulesDialogList () {
      const isZh = this.currentLang === 'zh'
      return [
        { zh: '30位候选人，5家公司，一次选满10票', id: 'Pilih 10 dari 5 perusahaan sekaligus' },
        { zh: '配额：IPIP 3、南建 1、华星 3、KNI 2、HBB 1', id: 'Kuota tetap per perusahaan' },
        { zh: '不支持分次投票', id: 'Tidak bisa bertahap' },
        { zh: '一钉钉账号一票，永久锁定', id: 'Satu akun, terkunci permanen' },
        { zh: '提交前不公开票数', id: 'Suara tersembunyi hingga submit' },
        { zh: '账号+设备指纹绑定，违规作废', id: 'Akun + device fingerprint' },
      ]
    },
    titleParts () {
      const title = this.$t('vote.title')
      if (this.currentLang === 'id') {
        const parts = title.split(' ')
        return [parts[0], parts.slice(1).join(' ')]
      }
      return [title]
    },
    selectedCompanies () {
      return this.companies.map(comp => {
        const candidates = comp.candidates.filter(c => this.isSelected(c.id))
        return {
          ...comp,
          count: candidates.length,
          candidates,
        }
      }).filter(c => c.count > 0)
    },
    allCompanies () {
      return this.companies.map(comp => ({
        ...comp,
        selectedCandidates: comp.candidates.filter(c => this.isSelected(c.id)),
      }))
    },
    detailVideo () {
      if (!this.detailCandidate) return ''
      return this.detailCandidate.videoUrl || require('../assets/normal video.mp4')
    },
  },
  methods: {
    $t (key) { return t(key) },
    getDeptText (candidate) {
      if (this.currentLang === 'zh') return candidate.dept
      return candidate.deptId || candidate.dept
    },
    getCompanyName (comp) {
      if (this.currentLang === 'zh') return comp.name
      return comp.idName || comp.key
    },
    getDescText (candidate) {
      if (this.currentLang === 'zh') return candidate.itemDesc
      return candidate.descId || candidate.itemDesc
    },
    switchLang (lang) {
      this.currentLang = lang
      try { setLang(lang) } catch (e) {}
      this.refreshStatus()
      this.$forceUpdate()
    },
    getCardStyle (idx) {
      return { '--card-index': idx }
    },
    getAvatar (candidate) {
      if (candidate && candidate.coverUrl) return candidate.coverUrl
      const code = candidate && candidate.code ? candidate.code : String(candidate)
      let h = 0
      for (let i = 0; i < code.length; i++) h = ((h << 5) - h) + code.charCodeAt(i)
      const isMale = Math.abs(h) % 2 === 0
      const idx = Math.abs(h) % 70
      const fallbackUrl = isMale
        ? 'https://randomuser.me/api/portraits/men/' + idx + '.jpg'
        : 'https://randomuser.me/api/portraits/women/' + idx + '.jpg'
      // 网络不可用或头像加载失败时使用本地默认头像
      return this.defaultAvatarImg || fallbackUrl
    },
    selectedByCompany (key) {
      const comp = this.companies.find(c => c.key === key)
      return comp ? comp.candidates.filter(c => this.selectedIds.includes(c.id)).length : 0
    },
    isSelected (id) { return this.selectedIds.includes(id) },
    mergeCandidates (serverCandidates) {
      return serverCandidates.map(serverC => {
        const realC = REAL_CANDIDATES.find(rc => rc.id === String(serverC.id))
        if (realC) {
          return {
            ...realC,
            ...serverC,
            name: realC.name,
            nameId: realC.nameId,
            code: realC.code,
            dept: realC.dept,
            deptId: realC.deptId,
            position: realC.position,
            itemDesc: realC.itemDesc,
            descId: realC.descId,
            coverUrl: realC.coverUrl,
            poster: realC.poster,
            videoUrl: realC.videoUrl,
            voteCount: serverC.voteCount
          }
        }
        return serverC
      })
    },
    toggleCandidate (c) {
      if (this.hasVoted) return
      if (!this.canVoteNow) {
        this.showToastMsg(this.currentLang === 'zh' ? '投票结束时间已过，已不能继续投票' : 'Waktu voting telah berakhir, tidak dapat melanjutkan voting')
        return
      }
      if (this.selectedIds.includes(c.id)) {
        this.selectedIds = this.selectedIds.filter(x => x !== c.id)
      } else {
        if (this.selectedCount >= 10) {
          this.$message.warning('最多选10位')
          return
        }
        // 公司配额检查
        const comp = this.companies.find(cp => cp.candidates.some(x => x.id === c.id))
        if (comp && this.selectedByCompany(comp.key) >= comp.max) {
          this.$message({
            message: this.currentLang === 'zh' ? comp.name + '已满' + comp.max + '票' : COMPANY_ID_MAP[comp.key] + ' kuota penuh ' + comp.max + ' suara',
            type: 'warning',
            center: true,
            duration: 2000
          })
          return
        }
        this.selectedIds.push(c.id)
      }
    },
    onTabClick (index) {
      this.bounceTab = index
      setTimeout(() => { this.bounceTab = -1 }, 400)
      this.scrollToCompany(index)
    },
    scrollToCompany (index) {
      this.activeCompany = index
      const ref = this.$refs['group-' + index]
      if (ref && ref[0]) {
        ref[0].scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    showDetail (c) {
      this.detailCandidate = c
    },
    voteFromDetail (c) {
      if (this.hasVoted) {
        this.detailCandidate = null
        return
      }
      if (!this.canVoteNow) {
        this.showToastMsg(this.currentLang === 'zh' ? '投票结束时间已过，已不能继续投票' : 'Waktu voting telah berakhir, tidak dapat melanjutkan voting')
        this.detailCandidate = null
        return
      }
      if (this.isSelected(c.id)) return
      this.toggleCandidate(c)
      this.detailCandidate = null
    },
    avatarStyle (c) {
      return { background: c.color }
    },
    submitClick (e) {
      if (this.hasVoted) {
        this.showPrizeRecord()
        return
      }
      if (!this.canVoteNow) {
        this.showToastMsg(this.currentLang === 'zh' ? '投票结束时间已过，已不能继续投票' : 'Waktu voting telah berakhir, tidak dapat melanjutkan voting')
        return
      }
      if (this.selectedCount < 10) return

      // 创建烟花粒子效果
      this.createFirework(e)

      // 延迟打开确认弹窗，让烟花先展示
      setTimeout(() => {
        this.showConfirm()
      }, 350)
    },
    createFirework (e) {
      const btn = this.$refs.submitBtn
      const rect = btn.getBoundingClientRect()
      const cx = e.clientX - rect.left
      const cy = e.clientY - rect.top

      const colors = [
        '#ff6b6b', '#ffd93d', '#6bcb77', '#4d96ff',
        '#ff6bff', '#ff9f43', '#00d2d3', '#f368e0',
        '#ff4757', '#2ed573', '#1e90ff', '#ffa502'
      ]

      // 第一波：主爆裂粒子（30颗）
      for (let i = 0; i < 36; i++) {
        const p = document.createElement('span')
        p.className = 'fw-particle'
        const size = 4 + Math.random() * 6
        const angle = (Math.PI * 2 * i) / 36 + (Math.random() - 0.5) * 0.15
        const dist = 50 + Math.random() * 90
        const dx = Math.cos(angle) * dist
        const dy = Math.sin(angle) * dist
        const color = colors[Math.floor(Math.random() * colors.length)]

        p.style.cssText = `
          width:${size}px; height:${size}px;
          left:${cx}px; top:${cy}px;
          background:${color};
          --dx:${dx}px; --dy:${dy}px;
          --s:${0.5 + Math.random() * 1.2};
          --d:${Math.random() * 0.15}s;
        `
        btn.appendChild(p)
        setTimeout(() => p.remove(), 900)
      }

      // 第二波：小星光粒子（15颗，延迟爆发）
      setTimeout(() => {
        for (let i = 0; i < 18; i++) {
          const p = document.createElement('span')
          p.className = 'fw-star'
          const size = 2 + Math.random() * 3
          const angle = Math.random() * Math.PI * 2
          const dist = 30 + Math.random() * 60
          const dx = Math.cos(angle) * dist
          const dy = Math.sin(angle) * dist
          const color = colors[Math.floor(Math.random() * colors.length)]

          p.style.cssText = `
            width:${size}px; height:${size}px;
            left:${cx}px; top:${cy}px;
            background:${color};
            --dx:${dx}px; --dy:${dy}px;
            --d:${Math.random() * 0.2}s;
          `
          btn.appendChild(p)
          setTimeout(() => p.remove(), 800)
        }
      }, 150)
    },
    showConfirm () {
      if (this.selectedCount < 10) return
      this.showConfirmDialog = true
    },
    submitVote () {
      this.showConfirmDialog = false
      const user = this.$store.state.user || {}
      const userName = user.userName || 'unknown'
      const voteData = {
        candidates: this.selectedIds.map(id => ({ id: id })),
        employeeId: userName
      }
      this.doSubmitVote(voteData, userName, 0)
    },
    doSubmitVote (voteData, userName, retryCount) {
      submitVoteApi(voteData).then(resp => {
        if (resp.data && resp.data.code === 200) {
          this.hasVoted = true
          // 刷新候选人列表，实时更新票数（合并前端真实人员静态信息）
          getCandidates().then(resp2 => {
            if (resp2.data && resp2.data.code === 200 && resp2.data.result) {
              this.candidates = this.mergeCandidates(resp2.data.result)
            }
          }).catch(() => {})
          this.$message({
            message: this.currentLang === 'zh' ? '投票成功，感谢您的参与🙏！' : 'Voting berhasil, terima kasih🙏！',
            type: 'success',
            center: true,
            duration: 2000,
            dangerouslyUseHTMLString: true
          })
          setTimeout(() => {
            this.openLottery()
          }, 2200)
        } else {
          const msg = resp.data && resp.data.message ? resp.data.message : ''
          // 后端 session 失效，尝试重新钉钉认证后重试提交
          if (retryCount < 1 && isInDingTalk() && (msg.indexOf('未登录') > -1 || msg.indexOf('login') > -1 || resp.data.code === 400)) {
            this.reAuthAndRetry(voteData, retryCount)
            return
          }
          this.$message({
            message: msg || (this.currentLang === 'zh' ? '投票失败' : 'Voting gagal'),
            type: 'error',
            center: true,
            duration: 3000
          })
        }
      }).catch(err => {
        const msg = (err.response && err.response.data && err.response.data.message)
          || (this.currentLang === 'zh' ? '网络错误，请稍后重试' : 'Kesalahan jaringan, coba lagi nanti')
        // 网络错误且可能是 session 问题，也尝试重新认证
        if (retryCount < 1 && isInDingTalk() && (msg.indexOf('未登录') > -1 || msg.indexOf('login') > -1)) {
          this.reAuthAndRetry(voteData, retryCount)
          return
        }
        this.$message({
          message: msg,
          type: 'error',
          center: true,
          duration: 3000
        })
      })
    },
    // 重新钉钉认证后重试提交
    async reAuthAndRetry (voteData, retryCount) {
      try {
        const corpId = this.$store.state.dingtalkCorpId || ''
        const code = await getAuthCode(corpId)
        const resp = await ssoLogin(code)
        if (resp.data && resp.data.code === 200) {
          // 重新认证成功，刷新了后端 session，重试提交
          const data = resp.data.result
          const userName = data.userName || data.name || ('user_' + data.mobile)
          this.$store.commit('login', { userName, loginType: 'dingtalk' })
          this.showToastMsg(this.currentLang === 'zh' ? '重新认证成功，正在提交...' : 'Autentikasi ulang berhasil, mengirim...', 1500)
          // 延迟一下等待 session 同步，然后重试提交
          setTimeout(() => {
            this.doSubmitVote(voteData, userName, retryCount + 1)
          }, 300)
        } else {
          this.$message({
            message: this.currentLang === 'zh' ? '重新认证失败，请稍后重试' : 'Autentikasi ulang gagal, coba lagi',
            type: 'error',
            center: true,
            duration: 3000
          })
        }
      } catch (err) {
        this.$message({
          message: this.currentLang === 'zh' ? '重新认证失败，请稍后重试' : 'Autentikasi ulang gagal, coba lagi',
          type: 'error',
          center: true,
          duration: 3000
        })
      }
    },
    openLottery () {
      const user = this.$store.state.user || {}
      const employeeId = user.userName || ''
      const self = this

      // 优先调用后端抽奖API
      drawLotteryV2(employeeId).then(resp => {
        const data = resp.data
        if (data && data.code === 200 && data.result) {
          self.prizeRecord = {
            won: data.result.won,
            level: data.result.level,
            amount: data.result.amount,
            name: data.result.name,
            nameId: data.result.nameId,
            winTime: data.result.winTime,
            claimStatus: data.result.claimStatus,
            claimed: data.result.claimStatus === 1
          }
          // 同步保存到 localStorage 兜底
          saveUserRecord(employeeId || 'unknown', self.prizeRecord)
          console.log('[Lottery] 后端抽奖成功:', self.prizeRecord.level, self.prizeRecord.amount)
        } else {
          // 后端抽奖失败，降级到前端本地抽奖
          fallbackDraw()
        }
      }).catch(err => {
        console.warn('[Lottery] 后端抽奖异常，降级到前端本地:', err.message)
        fallbackDraw()
      })

      // 前端本地抽奖降级
      function fallbackDraw () {
        const result = frontendDrawLottery(employeeId)
        self.prizeRecord = result
        saveUserRecord(employeeId || 'unknown', result)
        // 异步保存到后端数据库
        saveLotteryResult({
          level: result.level,
          amount: result.amount,
          employeeId: employeeId
        }).then(resp2 => {
          if (resp2.data && resp2.data.code === 200) {
            console.log('[Lottery] 降级保存到数据库成功')
          }
        }).catch(err2 => {
          console.warn('[Lottery] 降级保存到数据库失败:', err2.message)
        })
      }

      this.showLotteryDialog = true
    },
    showPrizeRecord () {
      // 如果已有 prizeRecord 则直接展示弹窗
      if (this.prizeRecord) {
        this.showPrizeDialog = true
        return
      }
      const user = this.$store.state.user || {}
      const userName = user.userName || 'unknown'
      const self = this

      // 优先从后端获取中奖记录
      getLotteryRecord().then(resp => {
        const data = resp.data
        if (data && data.code === 200 && data.result) {
          self.prizeRecord = {
            won: data.result.won,
            level: data.result.level,
            amount: data.result.amount,
            name: data.result.name,
            nameId: data.result.nameId,
            winTime: data.result.winTime,
            claimStatus: data.result.claimStatus,
            claimed: data.result.claimStatus === 1
          }
          // 同步到 localStorage
          saveUserRecord(userName, self.prizeRecord)
        } else {
          // 降级到 localStorage
          const record = getUserRecord(userName)
          if (record) {
            self.prizeRecord = record
          }
        }
      }).catch(() => {
        // 降级到 localStorage
        const record = getUserRecord(userName)
        if (record) {
          self.prizeRecord = record
        }
      })

      this.showPrizeDialog = true
    },
    handleClaimPrize () {
      const user = this.$store.state.user || {}
      const userName = user.userName || 'unknown'
      // 本地标记核销
      const success = claimPrize(userName)
      if (success && this.prizeRecord) {
        this.prizeRecord.claimed = true
        this.prizeRecord.claimStatus = 1
      }
      // 异步通知后端更新核销状态
      claimLotteryPrize().then(resp => {
        if (resp.data && resp.data.code === 200) {
          console.log('[Lottery] 核销状态已保存到数据库')
        }
      }).catch(err => {
        console.warn('[Lottery] 核销状态同步异常:', err.message)
      })
      this.showPrizeDialog = false
    },
    // 投票时间控制
    refreshStatus () {
      this.canVoteNow = canVote()
      this.statusInfo = getStatusInfo(this.currentLang)
    },
    showToastMsg (msg, duration) {
      this.toastMsg = msg
      this.showToast = true
      clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => {
        this.showToast = false
      }, duration || 2000)
    },
  },
  created () {
    // 打印当前加载的钉钉配置信息，用于排查测试环境配置是否正确
    const dingtalkConfig = {
      corpId: this.$store.state.dingtalkCorpId,
      env: process.env.VUE_APP_ENV,
      nodeEnv: process.env.NODE_ENV
    }
    console.log('[VotePage] 钉钉配置信息:', dingtalkConfig)

    // 从 URL 参数读取语言（事件失效时的最终兜底）
    const urlLang = new URLSearchParams(window.location.search).get('lang')
    if (urlLang === 'id' || urlLang === 'zh') {
      this.currentLang = urlLang
      try { setLang(urlLang) } catch (e) {}
      // 清除 URL 参数，避免刷新页面时重复触发
      const url = new URL(window.location)
      url.searchParams.delete('lang')
      window.history.replaceState({}, '', url)
    }

    this.refreshStatus()

    // 先用本地数据立即渲染卡片，避免页面白板
    this.candidates = REAL_CANDIDATES.slice()

    // 从后端获取候选人，并合并前端真实人员静态信息
    getCandidates().then(resp => {
      if (resp.data && resp.data.code === 200 && resp.data.result) {
        this.candidates = this.mergeCandidates(resp.data.result)
      }
    }).catch(() => {})

    // 检查是否已投票（无论userName是否为空都要检查，后端会从session获取当前用户）
    // 使用重试机制，确保认证完成后再判断
    const checkVotedWithRetry = (retries) => {
      checkVoted().then(resp => {
        if (resp.data && resp.data.code === 200 && resp.data.result && resp.data.result.voted) {
          this.hasVoted = true
          if (resp.data.result.selectedIds && Array.isArray(resp.data.result.selectedIds)) {
            this.selectedIds = resp.data.result.selectedIds
          }
          // 根据投票状态显示不同提示
          if (!this.canVoteNow) {
            this.showToastMsg(this.currentLang === 'zh'
              ? '您已完成投票，感谢参与！不可重复投票。'
              : 'Anda sudah menyelesaikan voting, terima kasih! Tidak dapat voting lagi.', 5000)
          } else {
            this.showToastMsg(this.currentLang === 'zh'
              ? '您已完成投票，感谢参与！不可重复投票。'
              : 'Anda sudah menyelesaikan voting, terima kasih! Tidak dapat voting lagi.', 5000)
          }
        }
        // 无论是否投票，都关闭加载遮罩
        this.pageLoading = false
      }).catch(() => {
        // 网络异常时重试（最多3次，间隔1秒）
        if (retries > 0) {
          console.warn('[checkVoted] 网络异常，' + retries + '秒后重试...')
          setTimeout(() => {
            checkVotedWithRetry(retries - 1)
          }, 1000)
        } else {
          // 重试耗尽，关闭加载遮罩，保持当前登录状态
          console.warn('[checkVoted] 重试耗尽，继续保持当前登录状态')
          this.pageLoading = false
        }
      })
    }
    checkVotedWithRetry(3)
    this.countdownTimer = setInterval(() => {
      this.refreshStatus()
    }, 1000)
  },
  mounted () {
    // 原生事件兜底：直接监听 <a> 标签的点击
    this.$nextTick(() => {
      const opts = this.$el.querySelectorAll('.lang-opt')
      opts.forEach(el => {
        el.addEventListener('click', (e) => {
          e.preventDefault()
          if (el.textContent.trim() === 'ID') this.switchLang('id')
          else this.switchLang('zh')
        })
        el.addEventListener('touchstart', (e) => {
          e.preventDefault()
          if (el.textContent.trim() === 'ID') this.switchLang('id')
          else this.switchLang('zh')
        })
      })
    })
  },
  beforeDestroy () {
    clearInterval(this.countdownTimer)
    clearTimeout(this.toastTimer)
  },
}
</script>

<style scoped>
.vote-page {
  min-height: 100vh;
  background: #fef9f0;
  padding-bottom: 180px;
  }
/* ===== 顶栏 ===== */
.top-bar {
  display: flex;
  flex-direction: column;
  padding: 6px 12px;
  background: #fff;
  border-bottom: 1px solid #f0e8d0;
  gap: 4px;
  }
.top-bar-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  }
.brand {
  font-size: 16px;
  font-weight: 700;
  color: #b8860b;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  flex-shrink: 0;
  }
.brand-logo {
  width: 60px;
  height: 30px;
  border-radius: 6px;
  object-fit: contain;
  }
.top-actions {
  display: flex;
  gap: 4px;
  align-items: center;
  flex-shrink: 0;
  }
.lang-switch {
  position: relative;
  display: inline-flex;
  align-items: center;
  background: #f0f0f0;
  border-radius: 18px;
  padding: 3px;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  }
.lang-opt {
  position: relative;
  z-index: 1;
  padding: 5px 16px;
  min-width: 36px;
  text-align: center;
  color: #999;
  font-size: 12px;
  transition: color 0.2s;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
  outline: none;
  -webkit-appearance: none;
  background: transparent;
  border: none;
  }
.lang-opt.active {
  color: #fff;
  font-weight: 700;
  }
.lang-slider {
  position: absolute;
  top: 3px;
  left: 3px;
  width: calc(50% - 3px);
  height: calc(100% - 6px);
  background: #daa520;
  border-radius: 15px;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 0;
  }
.lang-slider.slide-zh {
  transform: translateX(100%);
  }
.rule-btn {
  display: inline-flex; align-items: center; gap: 3px;
  padding: 5px 10px; border-radius: 20px; font-size: 12px; color: #b8860b;
  background: #fff8e1; cursor: pointer;
  white-space: nowrap;
}

/* ===== 倒计时标签 ===== */
.countdown-tag {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  padding: 5px 12px; border-radius: 16px;
  background: linear-gradient(135deg, rgba(218,165,32,0.12), rgba(255,215,0,0.08));
  border: 1px solid rgba(218,165,32,0.2);
  font-size: 11px;
}
.cd-label {
  color: #b8860b; font-weight: 600;
}
.cd-time {
  color: #8b6914; font-weight: 800; letter-spacing: 0.5px;
  font-variant-numeric: tabular-nums;
  font-family: 'SF Mono', 'Consolas', 'Menlo', monospace;
}

/* ===== 标题 — 霓虹跑马灯边框 ===== */
.header {
  text-align: center;
  padding: 16px 16px 12px;
  background: linear-gradient(180deg, #0a1628 0%, #0d1f3c 30%, #142952 60%, #0a1628 100%);
  position: relative;
  overflow: visible;
  margin: 0;
  /* 多层霓虹跑马灯光晕 */
  box-shadow:
    0 0 8px #ffd700,
    0 0 16px #ff8c00,
    0 0 24px #ffd700,
    0 0 32px #ffa500,
    0 0 48px rgba(255,140,0,0.6),
    inset 0 0 12px rgba(255,215,0,0.15);
  animation: header-marquee 5s ease-in-out infinite alternate;
}
@keyframes header-marquee {
  0% {
    box-shadow:
      0 0 8px #ffd700,
      0 0 16px #ff8c00,
      0 0 24px #ffd700,
      0 0 32px #ffa500,
      0 0 48px rgba(255,140,0,0.6),
      inset 0 0 12px rgba(255,215,0,0.15);
  }
  33% {
    box-shadow:
      0 0 12px #ffa500,
      0 0 22px #ffd700,
      0 0 34px #ff8c00,
      0 0 44px #ffd700,
      0 0 60px rgba(255,215,0,0.7),
      inset 0 0 18px rgba(255,140,0,0.2);
  }
  66% {
    box-shadow:
      0 0 8px #ff8c00,
      0 0 18px #ffa500,
      0 0 28px #ffd700,
      0 0 38px #ff8c00,
      0 0 52px rgba(255,165,0,0.65),
      inset 0 0 15px rgba(255,215,0,0.18);
  }
  100% {
    box-shadow:
      0 0 10px #ffd700,
      0 0 20px #ff8c00,
      0 0 30px #ffd700,
      0 0 40px #ffa500,
      0 0 56px rgba(255,140,0,0.7),
      inset 0 0 14px rgba(255,215,0,0.2);
  }
}
/* 跑马灯流光线条 — 上下边框 */
.header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg,
    transparent 0%, #ffd700 10%, #ffaa00 25%, #ffd700 40%,
    transparent 50%,
    #ffd700 60%, #ffaa00 75%, #ffd700 90%, transparent 100%
  );
  background-size: 200% 100%;
  animation: marquee-line 5s linear infinite;
  z-index: 2;
  pointer-events: none;
}
@keyframes marquee-line {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
/* 跑马灯流光线条 — 下边框 */
.header::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg,
    transparent 0%, #ffaa00 10%, #ffd700 25%, #ff8c00 40%,
    transparent 50%,
    #ff8c00 60%, #ffd700 75%, #ffaa00 90%, transparent 100%
  );
  background-size: 200% 100%;
  animation: marquee-line 5s linear infinite reverse;
  z-index: 2;
  pointer-events: none;
}
.header-glow {
  position: absolute;
  top: 50%; left: 50%;
  width: 280px; height: 280px;
  background: radial-gradient(circle, rgba(255,215,0,0.15) 0%, transparent 70%);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: glow-pulse 3s ease-in-out infinite;
}
@keyframes glow-pulse {
  0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); }
  50% { opacity: 1; transform: translate(-50%, -50%) scale(1.2); }
}
/* 金色粒子 */
.header-particle {
  position: absolute;
  width: 3px; height: 3px;
  background: #ffd700;
  border-radius: 50%;
  pointer-events: none;
  box-shadow: 0 0 6px #ffd700;
  animation: gold-float 4s ease-in-out infinite;
}
@keyframes gold-float {
  0%, 100% { transform: translateY(0) scale(1); opacity: 0.3; }
  50% { transform: translateY(-20px) scale(1.5); opacity: 0.8; }
}
.title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 25px;
  font-weight: 900;
  color: #ffd700;
  margin: 0;
  font-family: 'Helvetica Neue', Arial, 'PingFang SC', 'Microsoft YaHei', sans-serif;
  text-shadow: 0 0 20px rgba(255,215,0,0.5), 0 2px 8px rgba(0,0,0,0.3);
  letter-spacing: 3px;
  position: relative;
  flex-wrap: wrap;
  }
.title-trophy {
  flex-shrink: 0;
  font-size: 22px;
  }
.title-text {
  white-space: nowrap;
  }
.title-text-last {
  width: 100%;
  text-align: center;
  letter-spacing: 2px;
  }
.subtitle {
  font-size: 10px;
  color: rgba(255,215,0,0.7);
  margin: 6px 0 12px;
  letter-spacing: 1px;
  position: relative;
  text-transform: uppercase;
  }
.stats-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0;
  position: relative;
  z-index: 1;
}
.stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgba(255,255,255,0.95);
  border: 1px solid rgba(218,165,32,0.3);
  border-radius: 14px;
  padding: 6px 0;
  width: 100px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15), 0 0 12px rgba(255,215,0,0.08);
  transition: transform 0.2s;
}
.stat:active {
  transform: scale(0.95);
}
.stat-divider {
  width: 1px; height: 30px;
  background: rgba(218,165,32,0.3);
  margin: 0 12px;
}
.stat-num {
  font-size: 28px;
  font-weight: 800;
  color: #b8860b;
  text-shadow: 0 0 8px rgba(255,215,0,0.2);
  }
.stat-label {
  font-size: 10px;
  color: #8b7355;
  margin-top: 3px;
  }

/* ===== 配额标签 ===== */
.quota-tabs {
  display: flex;
  gap: 6px;
  padding: 10px 8px;
  background: linear-gradient(180deg, #fffdf5 0%, #fff 100%);
  border-bottom: 1px solid rgba(218,165,32,0.15);
  position: sticky;
  top: 0;
  z-index: 10;
  flex-wrap: nowrap;
  justify-content: space-between;
  overflow-x: hidden;
}
.quota-tab {
  padding: 6px 10px;
  border-radius: 16px;
  font-size: 11px;
  color: #8b7355;
  background: rgba(218,165,32,0.06);
  cursor: pointer;
  flex: 1;
  text-align: center;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(218,165,32,0.12);
  box-shadow: 0 2px 6px rgba(218,165,32,0.06);
  position: relative;
  user-select: none;
}
.quota-tab:active {
  transform: scale(0.88) translateY(2px);
  box-shadow: 0 0px 2px rgba(218,165,32,0.2);
}
.quota-tab.tab-bounce {
  animation: tab-bounce-anim 0.4s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
  background: rgba(255,215,0,0.15);
  border-color: rgba(255,215,0,0.35);
  color: #b8860b;
}
@keyframes tab-bounce-anim {
  0% { transform: scale(1); background: rgba(255,215,0,0.15); }
  30% { transform: scale(1.25); background: rgba(255,215,0,0.3); }
  50% { transform: scale(0.9); background: rgba(255,215,0,0.2); }
  70% { transform: scale(1.08); background: rgba(255,215,0,0.1); }
  100% { transform: scale(1); background: rgba(218,165,32,0.06); }
}
.quota-tab.active {
  background: linear-gradient(135deg, #daa520, #ffd700);
  color: #fff;
  border-color: transparent;
  box-shadow: 0 3px 12px rgba(218,165,32,0.4);
  transform: scale(1.02);
}
.quota-name {
  margin-right: 2px;
  }
.quota-tab em {
  font-style: normal;
  opacity: 0.8;
  }

/* ===== 候选人区域 ===== */
.candidate-area {
  padding: 8px 12px;
  scroll-behavior: smooth;
  }
.company-group {
  margin-bottom: 14px;
  }
.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 4px 6px 12px;
  position: relative;
}
.group-header::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 18px;
  background: linear-gradient(180deg, #daa520, #ffd700);
  border-radius: 2px;
  box-shadow: 0 0 6px rgba(255,215,0,0.4);
}
.group-name {
  font-size: 14px;
  font-weight: 700;
  color: #5c4a1e;
  }
.group-count {
  font-size: 12px;
  color: #b8860b;
  font-weight: 600;
  }
.candidate-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  }

/* ===== 卡片（金色荣耀风） ===== */
.candidate-card {
  background: #fff;
  border-radius: 14px;
  text-align: center;
  cursor: pointer;
  border: 2px solid transparent;
  position: relative;
  overflow: hidden;
  min-height: 130px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5px 6px 4px;
  box-shadow:
    0 2px 4px rgba(0,0,0,0.04),
    0 6px 16px rgba(0,0,0,0.06),
    0 12px 32px rgba(0,0,0,0.04);
  transition:
    transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 0.25s ease,
    border-color 0.25s ease,
    background 0.25s ease;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
}
.candidate-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255,255,255,0.25) 0%, transparent 45%, rgba(0,0,0,0.02) 100%);
  pointer-events: none;
  border-radius: inherit;
  z-index: 0;
}
.candidate-card::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 20%;
  right: 20%;
  height: 3px;
  background: transparent;
  border-radius: 3px 3px 0 0;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 1;
}
.candidate-card.selected::after {
  background: linear-gradient(90deg, #daa520, #ffd700, #ffec8b);
  left: 8%;
  right: 8%;
  box-shadow: 0 0 16px rgba(255,215,0,0.5), 0 0 32px rgba(255,215,0,0.2);
}
.candidate-card.selected {
  border-color: #daa520;
  background: #fffef5;
  transform: translateY(-3px);
  box-shadow:
    0 4px 8px rgba(218,165,32,0.1),
    0 8px 24px rgba(218,165,32,0.15),
    0 16px 40px rgba(218,165,32,0.08);
}
.candidate-card:active {
  animation: cardShake 0.3s ease;
  transition-duration: 0.08s;
}
.candidate-card.disabled {
  cursor: default;
}
@keyframes cardShake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-3px); }
  40% { transform: translateX(3px); }
  60% { transform: translateX(-2px); }
  80% { transform: translateX(2px); }
}
/* 卡片淡入 */
@keyframes cardFadeIn {
  0% { opacity: 0; transform: translateY(12px) scale(0.96); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}

/* ===== 电路纹理 SVG（金色） ===== */
.card-circuit {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
  opacity: 0.3;
}
.candidate-card.selected .card-circuit {
  opacity: 0.6;
}

/* ===== 悬浮晶体（金色） ===== */
.card-crystal {
  position: absolute;
  pointer-events: none;
  z-index: 0;
  border-radius: 2px;
  opacity: 0.12;
  animation: crystalFloat 6s ease-in-out infinite;
}
.crystal-1 {
  width: 10px; height: 10px;
  background: linear-gradient(135deg, rgba(218,165,32,0.6), rgba(255,215,0,0.3));
  top: 18%; right: 12%;
  transform: rotate(45deg);
  animation-delay: 0s;
}
.crystal-2 {
  width: 7px; height: 7px;
  background: linear-gradient(135deg, rgba(255,215,0,0.5), rgba(218,165,32,0.3));
  bottom: 28%; left: 10%;
  transform: rotate(45deg);
  animation-delay: 3s;
}
@keyframes crystalFloat {
  0%, 100% { transform: rotate(45deg) translateY(0); opacity: 0.08; }
  50% { transform: rotate(45deg) translateY(-6px); opacity: 0.2; }
}

/* ===== 底部反射 ===== */
.card-reflection {
  position: absolute;
  bottom: -12px;
  left: 10%;
  right: 10%;
  height: 20px;
  background: radial-gradient(ellipse at center, rgba(218,165,32,0.06) 0%, transparent 70%);
  pointer-events: none;
  z-index: 0;
  filter: blur(6px);
}
.candidate-card.selected .card-reflection {
  background: radial-gradient(ellipse at center, rgba(255,215,0,0.15) 0%, rgba(218,165,32,0.08) 50%, transparent 70%);
}

/* ===== 卡片顶部行（头像 + 详情按钮） ===== */
.card-header-row {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 2;
}

/* ===== 头像（放大） ===== */
.card-avatar-box {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5e6c8;
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
  border: 2px solid rgba(218,165,32,0.5);
  overflow: hidden;
  box-shadow:
    0 0 8px rgba(218,165,32,0.3),
    0 0 20px rgba(255,215,0,0.1),
    0 0 0 4px rgba(218,165,32,0.06);
}
.candidate-card.selected .card-avatar-box {
  border-color: rgba(218,165,32,0.9);
  box-shadow:
    0 0 12px rgba(218,165,32,0.4),
    0 0 30px rgba(255,215,0,0.2),
    0 0 0 6px rgba(218,165,32,0.1);
}

/* ===== 对勾 ===== */
.card-check {
  position: absolute;
  top: -2px;
  left: 26px;
  width: 18px;
  height: 18px;
  background: linear-gradient(135deg, #daa520, #ffd700);
  color: #fff;
  border-radius: 50%;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 6;
  box-shadow: 0 0 12px rgba(255,215,0,0.5), 0 0 24px rgba(218,165,32,0.2);
  animation: checkPop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes checkPop {
  0% { transform: scale(0); opacity: 0; }
  60% { transform: scale(1.3); }
  100% { transform: scale(1); opacity: 1; }
}

/* ===== 卡片信息 ===== */
.card-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 0 4px;
  padding-left: 4px;
  position: relative;
  z-index: 1;
  width: 100%;
  margin-top: 54px;
  margin-right: 0;
}
.card-name {
  font-size: 13px;
  color: #1a1a2e;
  font-weight: 700;
  line-height: 1.3;
  height: 17px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
  text-align: left;
}
.card-meta-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  max-width: 100%;
}
.card-code {
  font-size: 10px;
  color: #999;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  text-align: left;
}
.card-meta-dot {
  display: none;
}
.card-dept {
  font-size: 10px;
  color: #888;
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  text-align: left;
}

/* ===== 票数展示 ===== */
.card-votes {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  margin-top: 4px;
  padding: 2px 4px;
  background: linear-gradient(135deg, rgba(218,165,32,0.12), rgba(255,215,0,0.08));
  border: 1px solid rgba(218,165,32,0.2);
  border-radius: 10px;
  font-size: 11px;
  color: #b8860b;
  font-weight: 600;
  align-self: center;
}
.vote-icon {
  font-size: 10px;
}
.vote-count {
  font-variant-numeric: tabular-nums;
  min-width: 10px;
  text-align: center;
}
.vote-label {
  font-size: 10px;
  opacity: 0.9;
}

/* ===== 详情按钮（头像右侧） ===== */
.card-detail {
  height: 22px;
  padding: 0 8px;
  color: #b8860b;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff8e1;
  border-radius: 11px;
  transition: all 0.2s;
  z-index: 5;
  border: 1px solid rgba(218,165,32,0.2);
  box-shadow: 0 2px 6px rgba(0,0,0,0.08);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  margin-left: auto;
}
.card-detail:active {
  transform: scale(0.92);
  background: linear-gradient(135deg, #daa520, #ffd700);
  color: #fff;
}

/* ===== 规则提示 ===== */
.rules-tip {
  text-align: center;
  padding: 8px 16px;
  font-size: 10px;
  color: #8b7355;
  background: #fffdf5;
  border-top: 1px solid rgba(218,165,32,0.15);
}
.rules-tip p {
  margin: 0;
  line-height: 1.5;
 }

/* ===== 底部进度条 ===== */
.bottom-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  box-shadow: 0 -2px 12px rgba(0,0,0,0.06);
  padding: 10px 16px 14px;
  z-index: 100;
}
.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  color: #999;
  margin-bottom: 6px;
}
.progress-count {
  font-weight: 800;
  font-size: 13px;
  color: #b8860b;
}
/* 进度条轨道 */
.progress-bar-wrap {
  height: 8px;
  background: #e8e0f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
  position: relative;
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.06);
}
/* 进度条填充 */
.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #4c1d95, #5b4ce0, #7c3aed, #8b5cf6, #a78bfa);
  background-size: 300% 100%;
  border-radius: 4px;
  transition: width 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  animation: progressFlow 2s linear infinite;
}
/* 进度条发光 */
.progress-bar::after {
  content: '';
  position: absolute;
  top: -2px;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent);
  border-radius: 4px;
  animation: progressGlow 1.5s ease-in-out infinite;
}
@keyframes progressFlow {
  0% { background-position: 0% 50%; }
  100% { background-position: 300% 50%; }
}
@keyframes progressGlow {
  0%, 100% { opacity: 0.3; transform: translateX(-10%); }
  50% { opacity: 0.8; transform: translateX(10%); }
}
/* 进度百分比 */
.progress-percent {
  text-align: right;
  font-size: 10px;
  color: #1677ff;
  font-weight: 600;
  margin-bottom: 6px;
  margin-top: -4px;
}

/* 公司配额标签 */
.progress-quotas {
  display: flex;
  gap: 4px;
  font-size: 10px;
  color: #999;
  margin-bottom: 10px;
  flex-wrap: nowrap;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
.progress-quotas::-webkit-scrollbar {
  display: none;
}
.quota-pill {
  background: #fffdf5;
  border-radius: 16px;
  padding: 4px 10px;
  font-size: 10px;
  color: #8b7355;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 2px 6px rgba(218,165,32,0.08);
  user-select: none;
  font-weight: 500;
}
.quota-pill:active {
  transform: scale(1.15);
  background: linear-gradient(135deg, #daa520, #ffd700);
  color: #fff;
  box-shadow: 0 4px 12px rgba(218,165,32,0.4);
}
/* 配额小圆点（表示完成度） */
.quota-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.submit-btn {
  width: 100%;
  height: 44px;
  border: none;
  border-radius: 22px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 2px;
  background: #e0d5c0;
  color: #ad9a7a;
  cursor: not-allowed;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.submit-btn.active {
  background: linear-gradient(135deg, #daa520, #ffd700, #ffec8b);
  color: #5c4a1e;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(218,165,32,0.5), 0 0 30px rgba(255,215,0,0.2);
  position: relative;
  overflow: hidden;
  transform: translateY(0);
}
.submit-btn.active:active {
  transform: scale(0.92) translateY(2px);
  background: linear-gradient(135deg, #b8860b, #daa520);
  box-shadow: 0 1px 4px rgba(218,165,32,0.3);
}
.submit-btn.voted {
  background: linear-gradient(135deg, #1677ff, #4096ff);
  color: #fff;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(22,119,255,0.4);
  position: relative;
  overflow: hidden;
  transform: translateY(0);
}
.submit-btn.voted:active {
  transform: scale(0.92) translateY(2px);
  background: linear-gradient(135deg, #0958d9, #1677ff);
  box-shadow: 0 1px 4px rgba(22,119,255,0.3);
}

/* ===== 详情弹窗（全页面） ===== */
.detail-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 200;
  display: flex; align-items: center; justify-content: center;
}
.detail-page {
  background: #fff; border-radius: 20px; width: 92%; max-width: 360px;
  max-height: 90vh; overflow-y: auto; position: relative;
}
.detail-close {
  position: absolute; top: 10px; right: 14px; z-index: 10;
  width: 30px; height: 30px; border-radius: 50%; background: rgba(0,0,0,0.4);
  color: #fff; font-size: 16px; display: flex; align-items: center; justify-content: center;
  cursor: pointer;
}
.detail-video {
  background: #1a1a2e;
  border-radius: 20px 20px 0 0;
  overflow: hidden;
}
.detail-video-player {
  width: 100%;
  height: 360px;
  display: block;
  object-fit: cover;
  background: #1a1a2e;
}
.detail-body {
  padding: 20px 18px;
}
.detail-name {
  font-size: 22px; font-weight: 800; color: #1a1a2e; margin: 0 0 2px;
}
.detail-name-cn {
  font-size: 14px; font-weight: 500; color: #666; margin: 0 0 6px;
}
.detail-meta {
  font-size: 12px; color: #888; margin: 0; line-height: 1.5;
}
.detail-position {
  font-size: 12px; color: #b8860b; margin: 4px 0 0; font-weight: 500;
}
.detail-divider {
  height: 1px; background: #eee; margin: 14px 0;
}
.detail-desc {
  font-size: 13px; color: #555; line-height: 1.8; margin: 0;
  white-space: pre-line; text-align: left;
}
.detail-section-title {
  font-size: 14px; font-weight: 700; color: #333; margin: 0 0 6px;
}
.detail-video-hint {
  margin: 16px 0 0; text-align: center;
}
.video-hint-text {
  display: inline-block; font-size: 8px; color: #888;
  background: #f0f0f0; border-radius: 20px; padding: 8px 16px;
}
.detail-vote-area {
  padding: 0 18px 24px;
}
.detail-vote-btn {
  width: 100%; height: 48px; border: none; border-radius: 24px;
  background: linear-gradient(135deg, #1677ff, #4096ff); color: #fff;
  font-size: 17px; font-weight: 700; letter-spacing: 2px; cursor: pointer;
}

/* ===== 规则弹窗 ===== */
.rules-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 200;
  display: flex; align-items: flex-end; justify-content: center;
}
.rules-dialog {
  background: #fff; border-radius: 20px 20px 0 0; width: 100%; max-height: 80vh;
  overflow-y: auto; padding: 24px 20px 30px;
}
.rules-dialog-title { font-size: 18px; font-weight: 800; color: #1a1a2e; text-align: center; }
.rules-dialog-sub { font-size: 12px; color: #999; text-align: center; margin: 4px 0 16px; }
.rules-dialog-list { margin-bottom: 16px; }
.rules-dialog-item {
  display: flex; gap: 10px; padding: 10px 0; border-bottom: 1px dashed #eee;
  align-items: flex-start;
}
.rules-dialog-num {
  font-size: 18px; font-weight: 800; color: #1677ff; flex-shrink: 0;
  width: 30px; text-align: right;
}
.rules-dialog-body { flex: 1; }
.rules-dialog-text { font-size: 13px; color: #333; font-weight: 600; margin: 0; line-height: 1.4; }
.rules-dialog-lottery {
  text-align: center; font-size: 13px; color: #ff6b35; font-weight: 600;
  padding: 12px; background: #fff8f5; border-radius: 10px; margin-bottom: 16px;
  white-space: pre-line;
}
.rules-dialog-btn {
  width: 100%; height: 46px; border: none; border-radius: 23px;
  background: linear-gradient(135deg, #1677ff, #4096ff); color: #fff;
  font-size: 16px; font-weight: 700; cursor: pointer;
}

/* ===== 确认投票弹窗（金色荣耀风） ===== */
.confirm-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.55); z-index: 300;
  display: flex; align-items: center; justify-content: center;
  backdrop-filter: blur(2px);
}
.confirm-dialog {
  background: #fffef9; border-radius: 20px; width: 92%; max-width: 400px;
  max-height: 85vh; padding: 0;
  display: flex; flex-direction: column;
  overflow: hidden;
  position: relative;
  box-shadow: 0 8px 32px rgba(0,0,0,0.15), 0 0 0 1px rgba(218,165,32,0.1);
}
/* 顶部金色装饰条 */
.confirm-dialog::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 4px;
  background: linear-gradient(90deg, #b8860b, #daa520, #ffd700, #daa520, #b8860b);
  background-size: 200% 100%;
  animation: confirmBarFlow 5s linear infinite;
  z-index: 1;
}
@keyframes confirmBarFlow {
  0% { background-position: 200% 0; }
  100% { background-position: 0 0; }
}
.confirm-title {
  font-size: 19px; font-weight: 800; text-align: center;
  color: #8b6914; margin: 0; padding: 20px 20px 4px;
  flex-shrink: 0;
}
.confirm-sub {
  font-size: 12px; color: #c0392b; text-align: center; margin: 0 0 14px;
  font-weight: 500; flex-shrink: 0;
  padding: 0 20px;
}
.confirm-scroll-area {
  flex: 1; overflow-y: auto; min-height: 0;
  padding: 0 20px;
}
.confirm-list { margin-bottom: 10px; }
.confirm-company { margin-bottom: 12px; }
.confirm-company-header {
  padding: 6px 0; margin-bottom: 6px; text-align: left;
  border-bottom: 2px solid rgba(218,165,32,0.2);
  position: relative;
}
.confirm-company-header::before {
  content: '';
  position: absolute;
  left: 0; bottom: -2px;
  width: 40px; height: 2px;
  background: linear-gradient(90deg, #daa520, #ffd700);
}
.confirm-company-name {
  font-size: 14px; font-weight: 700;
  background: linear-gradient(135deg, #8b6914, #daa520);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.confirm-candidate {
  display: flex; align-items: center; gap: 10px; padding: 6px 0;
  border-bottom: 1px dashed rgba(218,165,32,0.12);
}
.confirm-cand-avatar {
  width: 44px; height: 44px; border-radius: 50%;
  flex-shrink: 0; position: relative; overflow: hidden;
  background-size: cover; background-position: center;
  border: 2px solid rgba(218,165,32,0.5);
  box-shadow:
    0 0 8px rgba(218,165,32,0.3),
    0 0 20px rgba(255,215,0,0.1),
    0 0 0 4px rgba(218,165,32,0.06);
}
.confirm-cand-info {
  flex: 1; display: flex; flex-direction: column; align-items: flex-start; line-height: 1.3;
}
.confirm-cand-name {
  font-size: 13px; color: #333; font-weight: 600;
}
.confirm-cand-name-id {
  font-size: 11px; color: #999;
}
.confirm-cand-code {
  font-size: 11px; color: #b8860b; flex-shrink: 0; margin-left: auto;
  font-weight: 500;
}
.confirm-lottery-hint {
  text-align: center; font-size: 12px; color: #8b6914; font-weight: 600;
  padding: 10px; background: linear-gradient(135deg, rgba(218,165,32,0.08), rgba(255,215,0,0.12));
  border-radius: 10px; margin-bottom: 14px;
  border: 1px solid rgba(218,165,32,0.15);
}
.confirm-btns {
  display: flex; gap: 10px; justify-content: center;
  padding: 0 20px 20px; flex-shrink: 0;
}
.confirm-btn-back {
  flex: 1; height: 42px; border: 1.5px solid rgba(218,165,32,0.3);
  border-radius: 21px; background: #fff; color: #8b6914; font-size: 13px;
  font-weight: 600; cursor: pointer; max-width: 160px;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}
.confirm-btn-back:active {
  transform: scale(0.95);
  background: #fef5e7;
  border-color: #daa520;
}
.confirm-btn-submit {
  flex: 1; height: 42px; border: none; border-radius: 21px;
  background: linear-gradient(135deg, #b8860b, #daa520, #ffd700);
  background-size: 200% 200%;
  color: #fff; font-size: 13px; font-weight: 700; cursor: pointer; max-width: 160px;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 16px rgba(218,165,32,0.4);
  animation: btnGoldFlow 3s ease infinite;
}
@keyframes btnGoldFlow {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}
.confirm-btn-submit:active {
  transform: scale(0.95);
  box-shadow: 0 2px 8px rgba(218,165,32,0.3);
}

/* ===== 页面加载遮罩 ===== */
.page-loading-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: #fef9f0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.page-loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f0e8d0;
  border-top-color: #daa520;
  border-radius: 50%;
  animation: loading-spin 0.8s linear infinite;
}
@keyframes loading-spin {
  to { transform: rotate(360deg); }
}
.page-loading-text {
  margin-top: 16px;
  font-size: 14px;
  color: #b8860b;
  font-weight: 500;
}
</style>

<style>
/* 烟花粒子 — 主爆裂 */
.submit-btn .fw-particle {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  z-index: 20;
  animation: fw-burst 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  animation-delay: var(--d, 0s);
  box-shadow: 0 0 4px currentColor;
}
@keyframes fw-burst {
  0% {
    transform: translate(0, 0) scale(1);
    opacity: 1;
  }
  60% {
    opacity: 1;
  }
  100% {
    transform: translate(var(--dx), var(--dy)) scale(var(--s, 0.5));
    opacity: 0;
  }
}

/* 烟花粒子 — 小星光 */
.submit-btn .fw-star {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  z-index: 20;
  animation: fw-star-burst 0.7s ease-out forwards;
  animation-delay: var(--d, 0s);
  box-shadow: 0 0 6px currentColor;
}
@keyframes fw-star-burst {
  0% {
    transform: translate(0, 0) scale(1);
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    transform: translate(var(--dx), var(--dy)) scale(0.2);
    opacity: 0;
  }
}
</style>

<style scoped>
/* ===== 开奖弹窗 / 中奖记录弹窗 ===== */
.lottery-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 400;
  display: flex; align-items: center; justify-content: center;
}
.lottery-card {
  background: #fff; border-radius: 20px; padding: 32px 24px 24px;
  text-align: center; width: 88%; max-width: 320px; position: relative;
}
.lottery-ribbon {
  font-size: 40px; margin-bottom: 10px;
}
.lottery-win-text {
  font-size: 20px; font-weight: 900; color: #1a1a2e; margin: 0 0 10px;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
}
.lottery-amount-text {
  font-size: 22px; font-weight: 900; color: #ff6b35; margin: 0 0 20px;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
}
.lottery-contact {
  font-size: 13px; color: #666; margin: -10px 0 20px; line-height: 1.5;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
}
.lottery-lose-text {
  font-size: 18px; font-weight: 700; color: #555; margin: 0 0 6px;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
}
.lottery-lose-sub {
  font-size: 14px; color: #888; margin: 0 0 24px;
}
.lottery-lose-emoji {
  font-size: 48px; margin: 0 0 20px;
}
.lottery-btn {
  width: 100%; height: 46px; border: none; border-radius: 23px;
  background: linear-gradient(135deg, #1677ff, #4096ff); color: #fff;
  font-size: 15px; font-weight: 700; cursor: pointer;
}
.claim-btn {
  background: linear-gradient(135deg, #52c41a, #73d13d);
  box-shadow: 0 4px 16px rgba(82,196,26,0.4);
}
.claim-btn:active {
  transform: scale(0.92);
  background: linear-gradient(135deg, #389e0d, #52c41a);
}
.claimed-btn {
  background: #d9d9d9 !important;
  color: #999 !important;
  cursor: not-allowed !important;
  box-shadow: none !important;
}

/* ===== Toast 提示 ===== */
.toast {
  position: fixed; bottom: 120px; left: 50%; transform: translateX(-50%);
  background: rgba(0,0,0,0.78); color: #fff; font-size: 13px;
  padding: 10px 24px; border-radius: 22px; z-index: 999;
  white-space: nowrap; font-weight: 500;
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);
}
.toast-fade-enter-active, .toast-fade-leave-active {
  transition: all 0.3s ease;
}
.toast-fade-enter, .toast-fade-leave-to {
  opacity: 0; transform: translateX(-50%) translateY(10px);
}
</style>


// WEBPACK FOOTER //
// src/components/VotePage.vue